import React, { useState, useEffect } from 'react';
import { Check, Cpu, Eye, Mail, Upload } from 'lucide-react';
import { useOrganization } from '@clerk/clerk-react';
import ApiService from '../../services/ApiService';
import toast from 'react-hot-toast';
import useBillingStore from '../../store/useBillingStore';

const UsageCard = ({ title, used = 0, limit = 0, Icon }) => {
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const exceeded = limit > 0 && used >= limit;
    const trackClasses = exceeded ? "bg-gray-100 border-gray-200" : "bg-gray-100 border-gray-200";
    const barClasses = exceeded ? "bg-black" : "bg-black";
    return (
        <div className="relative p-4 sm:p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow min-h-[120px] sm:min-h-[140px] flex flex-col justify-between overflow-visible">
            {exceeded && (
                <span className="absolute -top-2 -right-2 px-2 py-1 text-[10px] sm:text-[11px] font-semibold rounded-full bg-gray-100 text-black border border-gray-200 shadow-sm">
                    Exceeded
                </span>
            )}

            <div className="flex items-center gap-2 sm:gap-3">
                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                    {Icon && <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-black" />}
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-900 leading-5 truncate">{title}</h3>
                    <div className="mt-1 text-[10px] sm:text-xs text-gray-600">
                        <span className="font-semibold text-gray-900">{used}</span>
                        <span className="mx-1">/</span>
                        <span>{limit}</span>
                        {limit > 0 && <span className="ml-1 sm:ml-2 text-gray-400">({pct}%)</span>}
                    </div>
                </div>
            </div>

            <div className={`mt-1 h-2 rounded-full ${trackClasses} overflow-hidden`}>
                <div className={`h-full ${barClasses}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

const Billing = () => {
    const { organization } = useOrganization();
    const [subscription, setSubscription] = useState(null);
    const [organizationData, setOrganizationData] = useState(null);
    const { billingInfo } = useBillingStore();
    const [isLoading, setIsLoading] = useState(true);
    console.log('organizationData', organizationData);


    useEffect(() => {
        localStorage.setItem('loading', isLoading);
    }, [isLoading])

    useEffect(() => {
        const fetchData = async () => {
            if (!organization) return;
            try {
                const [subDetails, orgData] = await Promise.all([
                    ApiService.getSubscriptionDetails(),
                    ApiService.getOrganizationData(),
                ]);
                setSubscription(subDetails);
                setOrganizationData(orgData);
            } catch (error) {
                console.error('Failed to fetch billing data', error);
                // toast.error('Failed to load billing details.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [organization]);

    const tiers = [
        {
            name: 'Starter',
            description: 'Great for small teams getting started with AI support',
            price: '$0',
            period: '/month',
            trial: 'Life time free trial',
            buttonText: 'Request for life time access',
            buttonStyle: 'bg-white border border-gray-200 text-black hover:bg-gray-50',
            popular: false,
            features: [
                'Files: Text + PDF + DOCX (20 files, 20MB)',
                'Emails: 100 per month',
                'Tokens: 100,000 per month',
                'Regional Language: Auto Learning Mode',
                'AI Agent: BASIC',
                'FAQ Pages: Unlimited',
                'Widget Views: 10,000 per month',
                'Export/Backup: NA',
                'Support: Email support'
            ]
        },
        {
            name: 'Professional',
            description: 'For growing businesses that need more scale and insights',
            price: 'Custom',
            period: '',
            trial: '',
            buttonText: 'Talk to Sales',
            buttonStyle: 'bg-black text-white hover:bg-gray-800',
            popular: true,
            features: [
                'Files: All types (200 files, 100MB storage)',
                'Emails: 500 per month',
                'Tokens: 500,000 per month',
                'AI Agent: Custom training',
                'FAQ Pages: Unlimited',
                'Widget Views: 50,000 per month',
                'Export/Backup: Export as csv',
                'Support: Priority email + phone',
                'Regional Language: Auto Learning Mode',
                'Analytics: Advanced dashboard + reporting (coming soon)'
            ]
        },
        {
            name: 'Enterprise',
            description: 'Enterprise-grade package with dedicated services and security',
            price: 'Custom',
            period: '',
            trial: '',
            buttonText: 'Talk to Sales',
            buttonStyle: 'bg-white border border-gray-200 text-black hover:bg-gray-50',
            popular: false,
            features: [
                'Files: Unlimited (299MB+ storage)',
                'Emails: 2,000 per month',
                'Tokens: 20,000,000 per month',
                'AI Agent: Custom training + prompts',
                'FAQ Pages: Unlimited pages + SEO optimization',
                'Widget Views: 200,000 per month',
                'Export/Backup: Enterprise security + compliance',
                'Support: Dedicated account manager + phone',
                'Regional Language: Auto Learning Mode',
                'Features: Team collaboration + SSO + custom integrations'
            ]
        }
    ];

    const getButtonText = (tier) => {
        if (tier.name === 'Starter') {
            if (organizationData?.has_used_trial) {
                return 'Life Time Trial Claimed';
            }
            return tier.buttonText;
        }
        return tier.buttonText;
    };

    return (
        <div className="space-y-6 sm:space-y-8">
            <div className="w-full">
                <div className="text-center mb-6 sm:mb-12">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">Choose Your Plan</h1>
                    <p className="text-sm sm:text-base text-gray-600">Select the perfect plan for your business needs</p>
                </div>

                {/* 
                    Responsive Grid Layout:
                    - < 450px: 1 column
                    - 450px - 768px: 2 columns
                    - 768px - 950px: 1 column
                    - 950px - 1200px: 2 columns centered
                    - > 1200px: 3 columns
                */}
                <div className="grid grid-cols-1 min-[450px]:grid-cols-2 md:grid-cols-1 min-[950px]:grid-cols-2 min-[1200px]:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-7xl mx-auto">

                    {tiers.map((tier, index) => (
                        <div
                            key={index}
                            className={`relative bg-white rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-6 md:p-8 transition-all duration-300 hover:shadow-2xl w-full ${tier.popular ? 'border-2 sm:border-4 border-black lg:scale-105' : 'border border-gray-200'
                                }`}
                        >
                            {tier.popular && (
                                <div className="absolute -top-2.5 sm:-top-3 md:-top-4 left-1/2 transform -translate-x-1/2 z-10">
                                    <span className="bg-black text-white px-3 sm:px-4 md:px-6 py-1 sm:py-1.5 md:py-2 rounded-full text-[10px] sm:text-xs md:text-sm font-semibold shadow-lg whitespace-nowrap">
                                        Most Popular
                                    </span>
                                </div>
                            )}

                            <div className="mb-3 sm:mb-4 md:mb-6">
                                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1.5 sm:mb-2">{tier.name}</h2>
                                <p className="text-gray-600 text-[11px] sm:text-xs md:text-sm leading-relaxed">{tier.description}</p>
                            </div>

                            <div className="mb-3 sm:mb-4 md:mb-6">
                                <div className="flex items-baseline">
                                    <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">{tier.price}</span>
                                    {tier.period && <span className="text-gray-600 ml-1.5 sm:ml-2 text-xs sm:text-sm md:text-base">{tier.period}</span>}
                                </div>
                                {tier.trial && <p className="text-gray-500 text-[10px] sm:text-xs md:text-sm mt-1.5 sm:mt-2">{tier.trial}</p>}
                            </div>

                            <button
                                // disabled={isLoading || (tier.name === 'Starter' && (subscription || organizationData?.has_used_trial))}
                                onClick={async () => {
                                    if (tier.buttonText === 'Talk to Sales') {
                                        window.open('https://uttik.com/demo', '_blank');
                                    } else {
                                        if (!organization || !organization.id) {
                                            toast.error("Organization not found. Please try again.");
                                            return;
                                        }
                                        const today = new Date();
                                        const periodEndDate = new Date(today.setMonth(today.getMonth() + 1));
                                        const payload = {
                                            organization_id: organization.id,
                                            period_end_date: periodEndDate.toISOString().split('T')[0],
                                            usage_limit: {
                                                uploads_count: 20,
                                                emails: 100,
                                                tokens: 100000,
                                                widget_views: 10000,
                                            },
                                            is_trial: true,
                                        };
                                        try {
                                            await ApiService.createManualSubscription(payload);
                                            toast.success('Subscription created successfully!');
                                            // Refetch data to update UI
                                            const [subDetails, orgData] = await Promise.all([
                                                ApiService.getSubscriptionDetails(),
                                                ApiService.getOrganizationData(),
                                            ]);
                                            setSubscription(subDetails);
                                            setOrganizationData(orgData);
                                        } catch (error) {
                                            console.error('Failed to create manual subscription', error);
                                            toast.error('Failed to create subscription.');
                                        }
                                    }
                                }}
                                className={`w-full py-2 sm:py-2.5 md:py-3 px-3 sm:px-4 md:px-6 rounded-lg font-semibold text-xs sm:text-sm md:text-base transition-all duration-200 mb-4 sm:mb-6 md:mb-8 ${tier.buttonStyle} ${(isLoading || (tier.name === 'Starter' && (subscription || organizationData?.has_used_trial))) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <span className="block sm:hidden">{tier.buttonText === 'Request for life time access' ? 'Request Access' : tier.buttonText}</span>
                                <span className="hidden sm:block">{getButtonText(tier)}</span>
                            </button>

                            <div className="space-y-2 sm:space-y-2.5 md:space-y-3">
                                {tier.features.map((feature, idx) => (
                                    <div key={idx} className="flex items-start">
                                        <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-black mr-1.5 sm:mr-2 md:mr-3 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-700 text-[11px] sm:text-xs md:text-sm leading-relaxed">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div>
                    {billingInfo && (
                        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-100 mt-6 sm:mt-8">
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">Current Usage</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                                <UsageCard title="Uploads" used={billingInfo?.usage?.uploads_count || 0} limit={billingInfo?.limits?.uploads_count || 0} Icon={Upload} />
                                <UsageCard title="Emails per Month" used={billingInfo?.usage?.emails || 0} limit={billingInfo?.limits?.emails || 0} Icon={Mail} />
                                <UsageCard title="Tokens per Month" used={billingInfo?.usage?.tokens || 0} limit={billingInfo?.limits?.tokens || 0} Icon={Cpu} />
                                <UsageCard title="Widget Views per Month" used={billingInfo?.usage?.widget_views || 0} limit={billingInfo?.limits?.widget_views || 0} Icon={Eye} />
                            </div>
                        </div>
                    )}
                </div>

                {/* <div className="mt-16 text-center">
                    <p className="text-gray-600">
                        Need help choosing? <a href="#" className="text-blue-600 font-semibold hover:underline">Contact our team</a>
                    </p>
                </div> */}

            </div>
        </div>
    );
};

export default Billing;